import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useToast } from '../context/ToastContext';
import PromptInput from '../components/PromptInput';
import DataUploadContainer from '../components/DataUploadContainer';
import DataAnalysisContainer from '../components/DataAnalysisContainer';
import TargetSelectionContainer from '../components/TargetSelectionContainer';
import ModelingContainer from '../components/ModelingContainer';
import ExplainabilityContainer from '../components/ExplainabilityContainer';
import ActionableInsightsContainer from '../components/ActionableInsightsContainer';
import ChatPanel from '../components/ChatPanel';
import './AnalysisPage.css';

const sortDatasetsForUI = (datasets = []) => {
  return [...datasets].sort((a, b) => {
    const aIsRaw = a?.status === 'raw' ? 1 : 0;
    const bIsRaw = b?.status === 'raw' ? 1 : 0;
    if (aIsRaw !== bIsRaw) return aIsRaw - bIsRaw;

    const aTs = new Date(a?.created_at || 0).getTime();
    const bTs = new Date(b?.created_at || 0).getTime();
    return bTs - aTs;
  });
};

const normalizeAnalysisForUI = (analysisObj) => {
  if (!analysisObj) return analysisObj;
  return {
    ...analysisObj,
    datasets: sortDatasetsForUI(analysisObj.datasets || []),
  };
};

const AnalysisPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [showDataAnalysis, setShowDataAnalysis] = useState(false);
  const [showTargetSelection, setShowTargetSelection] = useState(false);
  const [showModeling, setShowModeling] = useState(false);
  const [showExplainability, setShowExplainability] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [modelStatusChangeCounter, setModelStatusChangeCounter] = useState(0);
  // eslint-disable-next-line no-unused-vars -- setters used by children
  const [analysisResults, setAnalysisResults] = useState(null);
  // eslint-disable-next-line no-unused-vars -- setters used by children
  const [correlationMatrix, setCorrelationMatrix] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [droppedColumns, setDroppedColumns] = useState([]);
  const [userContextChangeCounter, setUserContextChangeCounter] = useState(0);
  const [refreshModelStatusFn, setRefreshModelStatusFn] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [promptSubmitted, setPromptSubmitted] = useState(false);
  const [isSubmittingPrompt, setIsSubmittingPrompt] = useState(false);

  // Don't auto-show data analysis - wait for user to click button
  // Data analysis will only show when user explicitly clicks the button in DataUploadContainer

  // Don't auto-show target selection - wait for user to click button in DataAnalysisContainer
  // Target selection will only show when user explicitly clicks the button

  // Check if there are already trained models and SHAP data, and show containers accordingly
  useEffect(() => {
    const checkForExistingData = async () => {
      if (analysis?.datasets?.[0]?.id) {
        try {
          // First, check if there's a persisted target selection
          let persistedTargetName = null;
          try {
            const targetResponse = await apiService.getTargetSelectionPersist(analysis.datasets[0].id);
            persistedTargetName = targetResponse.data?.target_selection;

            if (persistedTargetName) {
              // Create a minimal target object with just the name
              setSelectedTarget({ name: persistedTargetName });
            }
          } catch (targetErr) {
            // No persisted target found
          }

          // If we have a persisted target, check if there's a model for it
          if (persistedTargetName) {
            const response = await apiService.getModelStatus(
              analysis.datasets[0].id,
              persistedTargetName
            );

            if (response.data.preliminary_result || response.data.full_result) {
              setShowModeling(true);

              // Check if SHAP data exists and show explainability container
              try {
                const shapResponse = await apiService.getShapStatus(
                  analysis.datasets[0].id,
                  persistedTargetName
                );

                if (shapResponse.data.shap_result) {
                  setShowExplainability(true);

                  // Check if insights exist and show insights container
                  try {
                    const insightsResponse = await apiService.getInsightsStatus(
                      analysis.datasets[0].id,
                      persistedTargetName
                    );

                    if (insightsResponse.data.insights_exist) {
                      setShowInsights(true);
                    }
                  } catch (insightsErr) {
                    // No insights exist yet
                  }
                }
              } catch (shapErr) {
                // No SHAP data exists yet
              }
            }
          }
        } catch (err) {
          // Silently fail - no models exist yet
        }
      }
    };

    checkForExistingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis?.datasets]);



  const isNewAnalysis = id === 'new';

  const { toast } = useToast();

  const fetchAnalysis = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getAnalysis(id);
      const normalized = normalizeAnalysisForUI(response.data);
      setAnalysis(normalized);
      setEditName(normalized.name);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        const msg = err.response?.data?.detail || 'You do not have access to this analysis.';
        toast(msg, 'error');
        navigate('/app', { replace: true });
      } else {
        setError('Failed to load analysis. Please try again.');
        console.error('Error fetching analysis:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [id, toast, navigate]);

  useEffect(() => {
    if (isNewAnalysis) {
      setAnalysis({
        name: 'Untitled Analysis',
        description: '',
        created_at: new Date().toISOString(),
        last_edited_at: new Date().toISOString(),
        datasets: [],
        user_context: ''
      });
      setEditName('Untitled Analysis');
      setLoading(false);
    } else {
      fetchAnalysis();
    }
  }, [id, isNewAnalysis, fetchAnalysis]);

  // Check if datasets exist OR if upload instruction exists, and set promptSubmitted accordingly
  // If there's user context but no datasets and no instruction, show the PromptInput with the existing context
  useEffect(() => {
    const checkPromptStatus = async () => {
      if (analysis?.datasets?.length > 0) {
        setPromptSubmitted(true);
        return;
      }

      // Check if upload instruction artifact exists
      if (analysis?.id) {
        try {
          const artifactsResp = await apiService.getArtifacts(analysis.id);
          const uploadArtifact = artifactsResp?.data?.artifacts?.find(
            art => art.page === 'upload' && art.kind === 'upload_instruction' && art.status === 'ready'
          );
          if (uploadArtifact) {
            setPromptSubmitted(true);
          }
        } catch (e) {
          // If error, don't auto-show DataUploadContainer
        }
      }
    };

    checkPromptStatus();
  }, [analysis?.datasets, analysis?.id]);

  // User-managed dropped columns have been removed; keep empty state.
  useEffect(() => {
    setDroppedColumns([]);
  }, [analysis?.datasets]);

  // Check for existing analysis data and auto-show completed sections
  useEffect(() => {
    if (!isNewAnalysis && analysis?.datasets?.[0]?.id && !loading) {
      const checkExistingData = async () => {
        try {
          const { apiService } = await import('../services/api');
          const statusResponse = await apiService.getAnalysisStatus(analysis.datasets[0].id);
          const status = statusResponse.data;
          
          // If analysis is complete, show DataAnalysisContainer
          if (status.status === 'complete' && status.analysis_data) {
            setShowDataAnalysis(true);
            
            // If target selection is complete, also show TargetSelectionContainer
            const targetResponse = await apiService.getTargetSelection(analysis.datasets[0].id);
            if (targetResponse.data) {
              setShowTargetSelection(true);
            }
          } else if (status.status === 'partial' && status.analysis_data) {
            // Partial analysis - show what we have
            setShowDataAnalysis(true);
          }
        } catch (err) {
          // If status check fails, don't auto-show anything
          console.log('No existing analysis data found');
        }
      };
      
      checkExistingData();
    }
  }, [isNewAnalysis, analysis?.datasets, loading]);

  const handleSaveName = async () => {
    try {
      if (isNewAnalysis) {
        // Create new analysis
        const response = await apiService.createAnalysis({
          name: editName,
          description: analysis.description
        });
        setAnalysis(normalizeAnalysisForUI(response.data));
        navigate(`/app/analysis/${response.data.id}`, { replace: true });
      } else {
        // Update existing analysis
        const response = await apiService.updateAnalysis(id, {
          name: editName
        });
        setAnalysis(normalizeAnalysisForUI(response.data));
      }
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving analysis name:', err);
      setError('Failed to save analysis name.');
    }
  };

  const handleCancelEdit = () => {
    setEditName(analysis.name);
    setIsEditing(false);
  };

  const handleBackToHome = () => {
    navigate('/app');
  };

  const handlePromptSubmit = async (prompt) => {
    setIsSubmittingPrompt(true);
    try {
      // Ensure analysis exists first
      let currentAnalysis = analysis;
      if (isNewAnalysis) {
        const nameToUse = (analysis?.name && analysis.name !== 'Untitled Analysis') ? analysis.name : 'Untitled Analysis';
        const resp = await apiService.createAnalysis({ name: nameToUse, description: analysis?.description || '' });
        currentAnalysis = normalizeAnalysisForUI(resp.data);
        setAnalysis(currentAnalysis);
        navigate(`/app/analysis/${currentAnalysis.id}`, { replace: true });
      }

      // Save the user context, generate name, and generate upload instruction in parallel
      const saveContextPromise = apiService.setUserContext(currentAnalysis.id, prompt);
      const uploadInstructionPromise = apiService.generateUploadInstruction(currentAnalysis.id, prompt);

      // Only generate name if current name is "Untitled Analysis"
      let nameGenerationPromise = null;
      if (currentAnalysis.name === 'Untitled Analysis') {
        nameGenerationPromise = apiService.generateAnalysisName(currentAnalysis.id, prompt);
      }

      // Wait for BOTH user context save and upload instruction generation before showing DataUploadContainer
      await Promise.all([
        saveContextPromise,
        uploadInstructionPromise
      ]);

      // Update local state with user context
      setAnalysis(prev => ({ ...prev, user_context: prompt }));

      // Show DataUploadContainer only after both responses are ready
      setPromptSubmitted(true);
      setIsSubmittingPrompt(false);

      // If we're generating a name, handle it with typing effect (can happen after showing container)
      if (nameGenerationPromise) {
        try {
          const nameResp = await nameGenerationPromise;
          const generatedName = nameResp.data.name;

          // Typing effect: reveal name character by character
          let currentIndex = 0;
          const typingInterval = setInterval(() => {
            currentIndex++;
            const partialName = generatedName.substring(0, currentIndex);
            setAnalysis(prev => ({ ...prev, name: partialName }));
            setEditName(partialName);

            if (currentIndex >= generatedName.length) {
              clearInterval(typingInterval);
              // Update the analysis name on the backend
              apiService.updateAnalysis(currentAnalysis.id, { name: generatedName });
            }
          }, 35); // 35ms per character for smooth typing effect
        } catch (err) {
          console.error('Failed to generate analysis name:', err);
          // Silently fail - keep "Untitled Analysis"
        }
      }
    } catch (err) {
      console.error('Failed to save user context:', err);
      setError('Failed to save your prompt. Please try again.');
      setIsSubmittingPrompt(false);
    }
  };

  if (loading) {
    return (
      <div className="analysis-page">
        <div className="container">
          <div className="analysis-page__loading">
            <div className="loading-spinner"></div>
            <p>Loading analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analysis-page">
        <div className="container">
          <div className="analysis-page__error">
            <h2>Something went wrong</h2>
            <p>{error}</p>
            <div className="analysis-page__error-actions">
              <button className="btn btn-primary" onClick={fetchAnalysis}>
                Try Again
              </button>
              <button className="btn btn-outline" onClick={handleBackToHome}>
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const primaryDatasetId = analysis?.datasets?.[0]?.id;

  return (
    <div className={`analysis-page ${isChatOpen ? 'analysis-page--chat-open' : ''}`}>
      <div className="container">
        <header className="analysis-page__header">
          <div className="home-arrow-container">
            <Link to="/app" className="back-to-home-arrow">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8"/>
              </svg>
              Home
            </Link>
          </div>

          <div className="analysis-page__title-section">
            {isEditing ? (
              <div className="analysis-page__edit-form">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="analysis-page__title-input"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveName();
                    } else if (e.key === 'Escape') {
                      handleCancelEdit();
                    }
                  }}
                />
                <div className="analysis-page__edit-actions">
                  <button className="btn btn-primary btn-sm" onClick={handleSaveName}>
                    Save
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="analysis-page__title-display">
                <h1
                  className="analysis-page__title"
                  onClick={() => setIsEditing(true)}
                  title="Click to edit"
                >
                  {analysis.name}
                </h1>
                <button
                  className="analysis-page__edit-btn"
                  onClick={() => setIsEditing(true)}
                  title="Edit analysis name"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="analysis-page__content">
          {!promptSubmitted ? (
            <PromptInput
              onSubmit={handlePromptSubmit}
              isLoading={isSubmittingPrompt}
              initialValue={analysis?.user_context || ''}
            />
          ) : (
            <>
              <DataUploadContainer
                analysis={analysis}
                isNewAnalysis={isNewAnalysis}
                modelStatusChangeCounter={modelStatusChangeCounter}
                ensureAnalysisExists={async () => {
                  // Ensure analysis exists: create only if user has changed name or is uploading
                  if (!isNewAnalysis) return analysis;
                  // If still 'Untitled Analysis' with no datasets, create on-demand with current name
                  const nameToUse = (analysis?.name && analysis.name !== 'Untitled Analysis') ? analysis.name : 'Untitled Analysis';
                  try {
                    const resp = await apiService.createAnalysis({ name: nameToUse, description: analysis?.description || '' });
                    setAnalysis(normalizeAnalysisForUI(resp.data));
                    navigate(`/app/analysis/${resp.data.id}`, { replace: true });
                    return resp.data;
                  } catch (err) {
                    if (err.response?.status === 403) {
                      const msg = err.response?.data?.detail || 'You have reached your analysis limit.';
                      toast(msg, 'error');
                    }
                    throw err;
                  }
                }}
                onDatasetCreated={(dataset) => {
                  setAnalysis((prev) => normalizeAnalysisForUI({ ...prev, datasets: [dataset, ...(prev?.datasets || [])] }));
                }}
                onReadyClick={() => setShowDataAnalysis(true)}
                onColumnsChanged={(dropColumns) => {
                  setDroppedColumns(dropColumns);
                }}
                onUserContextChanged={() => {
                  setUserContextChangeCounter(prev => prev + 1);
                }}
              />

          <DataAnalysisContainer
            analysis={analysis}
            visible={showDataAnalysis}
            droppedColumns={droppedColumns}
            onAnalysisComplete={(results) => {
              setAnalysisResults(results);
            }}
            onTargetSelectionReady={(correlationData) => {
              setCorrelationMatrix(correlationData);
              setShowTargetSelection(true);
            }}
          />

          <TargetSelectionContainer
            analysis={analysis}
            visible={showTargetSelection}
            userContextChangeCounter={userContextChangeCounter}
            droppedColumns={droppedColumns}
            onRefreshModelStatus={(refreshFn) => {
              setRefreshModelStatusFn(() => refreshFn);
            }}
            onTargetSelected={async (target) => {
              setSelectedTarget(target);

              // Check if there's already a model for this target
              if (target && analysis?.datasets?.[0]?.id) {
                try {
                  const response = await apiService.getModelStatus(
                    analysis.datasets[0].id,
                    target.name
                  );

                  // If there's a model for this target, show the ModelingContainer
                  if (response.data.preliminary_result || response.data.full_result) {
                    setShowModeling(true);

                    // Check if SHAP data exists and show explainability container
                    try {
                      const shapResponse = await apiService.getShapStatus(
                        analysis.datasets[0].id,
                        target.name
                      );

                      if (shapResponse.data.shap_result) {
                        setShowExplainability(true);

                        // Check if insights exist and show insights container
                        try {
                          const insightsResponse = await apiService.getInsightsStatus(
                            analysis.datasets[0].id,
                            target.name
                          );

                          if (insightsResponse.data.insights_exist) {
                            setShowInsights(true);
                          } else {
                            setShowInsights(false);
                          }
                        } catch (insightsErr) {
                          // No insights exist yet
                          setShowInsights(false);
                        }
                      } else {
                        setShowExplainability(false);
                        setShowInsights(false);
                      }
                    } catch (shapErr) {
                      // No SHAP data exists yet
                      setShowExplainability(false);
                      setShowInsights(false);
                    }
                  } else {
                    // No model for this target, hide ModelingContainer until user clicks "Train Prediction Model"
                    setShowModeling(false);
                    setShowExplainability(false);
                    setShowInsights(false);
                  }
                } catch (err) {
                  // No model exists yet, hide containers
                  setShowModeling(false);
                  setShowExplainability(false);
                  setShowInsights(false);
                }
              } else {
                // No target selected, hide containers
                setShowModeling(false);
                setShowExplainability(false);
                setShowInsights(false);
              }
            }}
            onReadyClick={() => {
              setShowModeling(true);
            }}
          />

          <ModelingContainer
            analysis={analysis}
            visible={showModeling}
            selectedTarget={selectedTarget}
            droppedColumns={droppedColumns}
            onReadyClick={() => {
              setShowExplainability(true);
            }}
            onModelStatusChange={(targetName, status) => {
              // When a model finishes training, refresh the model status in TargetSelectionContainer
              if (refreshModelStatusFn) {
                refreshModelStatusFn();
              }
              // Also inform upload section immediately so lock state updates without page refresh
              setModelStatusChangeCounter((prev) => prev + 1);
            }}
          />

          <ExplainabilityContainer
            analysis={analysis}
            visible={showExplainability}
            selectedTarget={selectedTarget}
            droppedColumns={droppedColumns}
            onReadyClick={() => {
              setShowInsights(true);
            }}
          />

          <ActionableInsightsContainer
            analysis={analysis}
            visible={showInsights}
            selectedTarget={selectedTarget}
          />
            </>
          )}
        </main>

        <ChatPanel
          analysisId={analysis?.id}
          hasDataset={Boolean(primaryDatasetId)}
          onOpenChange={setIsChatOpen}
        />
      </div>
    </div>
  );
};

export default AnalysisPage;